import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, errDetail, API } from "../lib/api";
import { useApp } from "../lib/store";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Tag, TrendingUp, ShoppingCart, ShieldCheck, FileText, BarChart3, Users, ArrowRight, Sparkles } from "lucide-react";

function MsLogo({ className = "" }) {
  return (
    <svg viewBox="0 0 21 21" className={className} width="16" height="16" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function LoginForm() {
  const { setUser, t } = useApp();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [newPw, setNewPw] = useState("");
  const [inviteToken, setInviteToken] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [idp, setIdp] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  useEffect(() => {
    api.get("/auth/sso/status").then((r) => setSsoEnabled(!!r.data?.enabled)).catch(() => {});
    const p = new URLSearchParams(window.location.search);
    const inv = p.get("invite");
    const rst = p.get("reset_token");
    if (inv) {
      setInviteToken(inv);
      api.get(`/auth/invitation/${inv}`)
        .then((r) => { setEmail(r.data.email); setStep("accept_invite"); })
        .catch((e) => { setError(errDetail(e)); });
    } else if (rst) {
      setResetToken(rst);
      setStep("set_password");
    }
  }, []);

  const checkEmail = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login-methods", { email });
      if (data.method === "sso" && data.idp) {
        setIdp(data.idp);
        if (data.sso_required) {
          window.location.href = `${API}/auth/oidc/${data.idp.id}/login`;
          return;
        }
        setStep("password");
      } else {
        setStep("password");
      }
    } catch (err) { setError(errDetail(err)); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setStep("mfa");
        return;
      }
      localStorage.setItem("lf_token", data.access_token);
      if (data.mfa_setup_required) toast.info(t("login_mfa_setup_hint"));
      setUser(data.user);
    } catch (err) { setError(errDetail(err)); }
    finally { setLoading(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/verify-login", { mfa_token: mfaToken, code });
      localStorage.setItem("lf_token", data.access_token);
      setUser(data.user);
    } catch (err) { setError(errDetail(err)); }
    finally { setLoading(false); }
  };

  const requestReset = async () => {
    setLoading(true);
    try {
      await api.post("/auth/password/reset-request", { email });
      toast.success(t("login_reset_sent"));
      setStep("email");
    } catch (err) { setError(errDetail(err)); }
    finally { setLoading(false); }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      if (inviteToken) {
        const { data } = await api.post(`/auth/invitation/${inviteToken}/accept`, { password: newPw });
        localStorage.setItem("lf_token", data.access_token);
        window.history.replaceState({}, "", "/");
        setUser(data.user);
      } else {
        await api.post("/auth/password/reset-confirm", { token: resetToken, password: newPw });
        toast.success(t("login_pw_set"));
        window.history.replaceState({}, "", "/login");
        setStep("email");
      }
    } catch (err) { setError(errDetail(err)); }
    finally { setLoading(false); }
  };

  const inputCls = "h-10 bg-white text-gray-900 placeholder:text-gray-400";
  const btnCls = "w-full h-10 bg-[#1B2A4A] hover:bg-[#2a3d63] text-white font-semibold transition-colors";
  const ssoBtnCls = "w-full h-10 bg-white hover:bg-gray-50 border border-gray-300 text-gray-800 font-semibold transition-colors flex items-center justify-center gap-2";
  const dividerCls = "text-gray-400";
  const dividerLineCls = "bg-gray-200";
  const linkCls = "text-gray-500 hover:text-[#1B2A4A]";

  if (step === "mfa") {
    return (
      <form onSubmit={submitMfa} className="space-y-4" data-testid="login-mfa-form">
        <p className="text-sm text-gray-600">{t("login_mfa_title")}</p>
        <div className="space-y-1.5">
          <Label>{t("login_mfa_code")}</Label>
          <Input data-testid="login-mfa-input" value={code} onChange={(e) => setCode(e.target.value)}
            inputMode="numeric" placeholder="123456" required className={inputCls} />
        </div>
        {error && <p className="text-sm text-red-500 font-medium" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="login-mfa-submit-btn" className={btnCls}>
          {loading ? "…" : t("login_verify")}
        </Button>
        <button type="button" className={`text-xs ${linkCls}`} onClick={() => setStep("email")} data-testid="login-back-btn">
          ← {t("login_back")}
        </button>
      </form>
    );
  }

  if (step === "accept_invite" || step === "set_password") {
    return (
      <form onSubmit={submitNewPassword} className="space-y-4" data-testid="login-setpw-form">
        <p className="text-sm text-gray-600">
          {step === "accept_invite" ? t("login_invite_welcome") : t("login_set_password")}
        </p>
        <div className="space-y-1.5">
          <Label>E-Mail</Label>
          <Input value={email} disabled className={inputCls} data-testid="login-setpw-email" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("prof_new_pw")}</Label>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required
            className={inputCls} data-testid="login-setpw-input" />
          <p className={`text-[11px] ${dividerCls}`}>{t("login_pw_rules")}</p>
        </div>
        {error && <p className="text-sm text-red-500 font-medium" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="login-setpw-submit-btn" className={btnCls}>
          {loading ? "…" : t("login_pw_save")}
        </Button>
      </form>
    );
  }

  if (step === "email") {
    return (
      <form onSubmit={checkEmail} className="space-y-4" data-testid="login-email-form">
        <div className="space-y-1.5">
          <Label>E-Mail</Label>
          <Input data-testid="login-email-input" type="email" value={email} autoComplete="username"
            onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
        </div>
        {error && <p className="text-sm text-red-500 font-medium" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="login-continue-btn" className={btnCls}>
          {loading ? "…" : t("login_continue")}
        </Button>
        {ssoEnabled && (
          <>
            <div className="flex items-center gap-3 py-1">
              <div className={`h-px flex-1 ${dividerLineCls}`} />
              <span className={`text-xs uppercase tracking-widest ${dividerCls}`}>oder</span>
              <div className={`h-px flex-1 ${dividerLineCls}`} />
            </div>
            <button type="button" data-testid="sso-microsoft-btn" className={ssoBtnCls + " rounded-md"}
              onClick={() => { window.location.href = `${API}/auth/microsoft/login`; }}>
              <MsLogo /> <SsoLabel />
            </button>
          </>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-password-form">
      <div className="space-y-1.5">
        <Label>E-Mail</Label>
        <Input data-testid="login-email-input" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
      </div>
      <div className="space-y-1.5">
        <Label>Password</Label>
        <Input data-testid="login-password-input" type="password" value={password} autoFocus
          onChange={(e) => setPassword(e.target.value)} required className={inputCls} />
      </div>
      {error && <p className="text-sm text-red-500 font-medium" data-testid="login-error">{error}</p>}
      <Button type="submit" disabled={loading} data-testid="login-submit-btn" className={btnCls}>
        {loading ? "…" : "Sign in"}
      </Button>
      <div className="flex items-center justify-between">
        <button type="button" className={`text-xs ${linkCls}`} onClick={() => setStep("email")} data-testid="login-back-btn">
          ← {t("login_back")}
        </button>
        <button type="button" className={`text-xs ${linkCls}`} onClick={requestReset} data-testid="login-forgot-btn">
          {t("login_forgot")}
        </button>
      </div>
      {idp && (
        <>
          <div className="flex items-center gap-3 py-1">
            <div className={`h-px flex-1 ${dividerLineCls}`} />
            <span className={`text-xs uppercase tracking-widest ${dividerCls}`}>oder</span>
            <div className={`h-px flex-1 ${dividerLineCls}`} />
          </div>
          <button type="button" data-testid="sso-idp-btn" className={ssoBtnCls + " rounded-md"}
            onClick={() => { window.location.href = `${API}/auth/oidc/${idp.id}/login`; }}>
            <MsLogo /> {idp.name}
          </button>
        </>
      )}
      {ssoEnabled && !idp && (
        <>
          <div className="flex items-center gap-3 py-1">
            <div className={`h-px flex-1 ${dividerLineCls}`} />
            <span className={`text-xs uppercase tracking-widest ${dividerCls}`}>oder</span>
            <div className={`h-px flex-1 ${dividerLineCls}`} />
          </div>
          <button type="button" data-testid="sso-microsoft-btn" className={ssoBtnCls + " rounded-md"}
            onClick={() => { window.location.href = `${API}/auth/microsoft/login`; }}>
            <MsLogo /> <SsoLabel />
          </button>
        </>
      )}
    </form>
  );
}

function SsoLabel() {
  const { t } = useApp();
  return <>{t("sso_signin")}</>;
}

function LangSwitch({ lang, setLang, tone = "light" }) {
  const dark = tone === "dark";
  return (
    <div className="inline-flex items-center gap-1 text-xs font-semibold">
      {["en", "de", "es", "pt", "it"].map((l) => (
        <button key={l} data-testid={`landing-lang-${l}`} onClick={() => setLang(l)}
          className={`px-2 h-7 rounded-md uppercase transition-colors ${
            lang === l
              ? (dark ? "bg-[#F5A623] text-[#1B2A4A]" : "bg-[#1B2A4A] text-white")
              : (dark ? "text-white/60 hover:text-white" : "text-gray-500 hover:text-[#1B2A4A]")
          }`}>
          {l}
        </button>
      ))}
    </div>
  );
}

function BrandMark({ tone = "light" }) {
  const dark = tone === "dark";
  return (
    <div className="flex items-center gap-2">
      <div className="h-10 w-10 rounded bg-[#F5A623] flex items-center justify-center">
        <Tag className="h-5 w-5 text-[#1B2A4A]" strokeWidth={2.5} />
      </div>
      <div>
        <div className={`font-bold text-lg tracking-tight leading-none ${dark ? "text-[#1B2A4A]" : "text-white"}`} style={{ fontFamily: "Work Sans" }}>Encasa</div>
        <div className={`text-[10px] uppercase tracking-widest ${dark ? "text-gray-400" : "text-white/50"}`}>CRM &amp; SRM</div>
      </div>
    </div>
  );
}

function Landing() {
  const { t, lang, setLang } = useApp();
  const features = [
    { icon: TrendingUp, t: t("lp_f1_t"), d: t("lp_f1_d") },
    { icon: ShoppingCart, t: t("lp_f2_t"), d: t("lp_f2_d") },
    { icon: ShieldCheck, t: t("lp_f3_t"), d: t("lp_f3_d") },
    { icon: FileText, t: t("lp_f4_t"), d: t("lp_f4_d") },
    { icon: BarChart3, t: t("lp_f5_t"), d: t("lp_f5_d") },
    { icon: Users, t: t("lp_f6_t"), d: t("lp_f6_d") },
  ];
  const modules = [
    t("nav_group_sales"), t("nav_group_customers"), t("nav_group_purchasing"),
    t("nav_group_service"), t("nav_group_contracts"), t("nav_group_insights"),
  ];
  const stats = [
    { v: "360°", l: t("lp_stat1_l") },
    { v: "5", l: t("lp_stat2_l") },
    { v: "8D", l: t("lp_stat3_l") },
    { v: "SRM", l: t("lp_stat4_l") },
  ];
  const scrollToLogin = () => document.getElementById("login-card")?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div className="min-h-screen bg-[#0d1830] text-white relative overflow-hidden">
      {/* background layers */}
      <div className="pointer-events-none absolute inset-0 grain opacity-[0.5]" />
      <div className="pointer-events-none absolute -right-32 -top-24 h-[32rem] w-[32rem] rounded-full bg-[#F5A623]/20 blur-[120px] animate-float-slow" />
      <div className="pointer-events-none absolute -left-40 top-1/3 h-[28rem] w-[28rem] rounded-full bg-[#3B82F6]/20 blur-[120px] animate-float-slower" />
      <div className="pointer-events-none absolute right-1/4 bottom-0 h-72 w-72 rounded-full bg-[#F5A623]/10 blur-[100px]" />

      {/* top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-5">
        <BrandMark tone="light" />
        <div className="flex items-center gap-4">
          <LangSwitch lang={lang} setLang={setLang} tone="dark" />
          <button data-testid="landing-nav-signin-btn" onClick={scrollToLogin}
            className="hidden sm:inline-flex h-9 items-center rounded-full bg-[#F5A623] px-5 text-sm font-semibold text-[#1B2A4A] hover:bg-[#ffb838] transition-colors">
            {t("lp_nav_signin")}
          </button>
        </div>
      </header>

      {/* hero */}
      <section className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 pt-8 pb-16 lg:pt-14 lg:pb-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5 text-[#F5A623]" strokeWidth={2.4} />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-white/70">{t("lp_eyebrow")}</span>
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" style={{ fontFamily: "Work Sans" }}>
            {t("lp_title")}
          </h1>
          <p className="mt-5 max-w-xl text-base sm:text-lg text-white/60 leading-relaxed">{t("lp_desc")}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button data-testid="landing-hero-signin-btn" onClick={scrollToLogin}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-[#F5A623] px-6 text-sm font-semibold text-[#1B2A4A] hover:bg-[#ffb838] transition-colors shadow-lg shadow-[#F5A623]/20">
              {t("lp_cta")} <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
            </button>
            <a href="#features"
              className="inline-flex h-11 items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 text-sm font-semibold text-white hover:bg-white/10 transition-colors backdrop-blur-sm">
              {t("lp_explore")}
            </a>
          </div>

          {/* stats */}
          <div className="mt-10 grid grid-cols-4 gap-3 max-w-xl">
            {stats.map((s) => (
              <div key={s.l} className="rounded-xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur-sm">
                <div className="text-xl sm:text-2xl font-bold text-[#F5A623]" style={{ fontFamily: "Work Sans" }}>{s.v}</div>
                <div className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-wide text-white/50 leading-tight">{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* login card */}
        <motion.div id="login-card" data-testid="landing-login-card"
          initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="w-full max-w-md justify-self-center lg:justify-self-end">
          <div className="rounded-2xl bg-white p-7 sm:p-8 shadow-2xl shadow-black/40 ring-1 ring-black/5">
            <h2 className="text-xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("lp_signin_card_title")}</h2>
            <p className="mt-1 mb-6 text-sm text-gray-500">{t("login_sub")}</p>
            <LoginForm />
          </div>
        </motion.div>
      </section>

      {/* core functions */}
      <section id="features" className="relative z-10 border-t border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 py-16 lg:py-20">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Work Sans" }}>{t("lp_features_title")}</h2>
            <p className="mt-3 text-white/55 text-base sm:text-lg leading-relaxed">{t("lp_features_sub")}</p>
          </motion.div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {features.map((f, i) => (
              <motion.div key={f.t} data-testid={`landing-feature-${i + 1}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -6 }}
                className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-colors hover:border-[#F5A623]/40 hover:bg-white/[0.07]">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F5A623]/15 border border-[#F5A623]/30 group-hover:bg-[#F5A623]/25 transition-colors">
                  <f.icon className="text-[#F5A623]" style={{ height: 22, width: 22 }} strokeWidth={2.1} />
                </div>
                <div className="mt-5 text-lg font-semibold text-white">{f.t}</div>
                <div className="mt-2 text-sm text-white/55 leading-relaxed">{f.d}</div>
              </motion.div>
            ))}
          </div>

          {/* module chips */}
          <div className="mt-12 flex flex-wrap gap-2.5">
            {modules.map((m) => (
              <span key={m} className="text-[12px] font-medium text-white/65 bg-white/5 border border-white/10 rounded-full px-4 py-1.5">
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* footer */}
      <footer className="relative z-10 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <BrandMark tone="light" />
          <div className="text-white/30 text-xs">© 2026 Encasa CRM &amp; SRM</div>
        </div>
      </footer>
    </div>
  );
}

export default function Login() {
  const { setUser } = useApp();

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const tok = p.get("sso_token");
    const err = p.get("sso_error");
    if (tok) {
      localStorage.setItem("lf_token", tok);
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
      window.history.replaceState({}, "", "/login");
    } else if (err) {
      const map = {
        nouser: "Kein CRM-Benutzer mit dieser E-Mail-Adresse gefunden. Bitte wenden Sie sich an Ihren Administrator.",
        domain: "Diese E-Mail-Domain ist für die Anmeldung nicht zugelassen.",
        state: "Die Anmeldesitzung ist abgelaufen. Bitte versuchen Sie es erneut.",
        exchange: "Die Microsoft-Anmeldung ist fehlgeschlagen.",
        config: "Microsoft SSO ist derzeit nicht konfiguriert.",
        noemail: "Von Microsoft wurde keine E-Mail-Adresse übermittelt.",
      };
      toast.error(map[err] || "Die Microsoft-Anmeldung ist fehlgeschlagen.");
      window.history.replaceState({}, "", "/login");
    }
  }, [setUser]);

  return <Landing />;
}
